import { forwardRef, ForwardRefRenderFunction, useState } from 'react';

import { usersQueryKeys } from '@apis/usersApi';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';
import { Button } from '@shared/components/button-new';
import { Lookup, LookupProps } from '@shared/components/lookups/lookup';
import { BasicUser, NewUser } from '@shared/models/user';
import { InputSize } from '@shared/types/common/input';

import { UserLookupCard } from './user-lookup-card';
import { styles } from './UserLookup.styles';

export type ExistingUserLookupItem = Pick<BasicUser, 'id' | 'image' | 'name' | 'theme'>;

export type MixedUser = ExistingUserLookupItem | NewUser;

export const isUserValueSame = (item: MixedUser, value: MixedUser) => {
  if ('id' in item && 'id' in value) {
    return item.id === value.id;
  }
  if ('name' in item && 'name' in value) {
    return item.name === value.name;
  }
  return false;
};

// ******************************************************
// * !!        DO NOT USE COMPONENT DIRECTLY         !! *
// * !! USE UserSingleLookup / UserMultiLookup !! *
// ******************************************************

export interface UserLookupProps
  extends Omit<
      LookupProps,
      | 'classes'
      | 'localSearch'
      | 'placeholder'
      | 'getActionRow'
      | 'onCreate'
      | 'queryFactory'
      | 'renderOption'
      | 'setLocalSearch'
    >,
    WithStyles<typeof styles> {
  initialSearch?: string;
  lookupClasses?: Partial<LookupProps['classes']>;
  placeholder?: string;
  onCreate?: (value: NewUser) => void;
}

const UserLookupComponent: ForwardRefRenderFunction<AutocompleteRef, UserLookupProps> = (
  { classes, initialSearch, lookupClasses, onCreate, onSelect, ...lookupProps },
  ref
) => {
  const [localSearch, setLocalSearch] = useState<string>(() => initialSearch || '');

  return (
    <Lookup
      {...lookupProps}
      classes={lookupClasses}
      localSearch={localSearch}
      placeholder={t('xtextx_ellipsis', { text: lookupProps.placeholder || t('search_for_a_user') })}
      ref={ref}
      getActionRow={(search, clearSearch) => {
        if (!search || !onCreate) {
          return undefined;
        }

        return (
          <Button
            classes={{ root: classes.createBtn, buttonLabel: classes.createBtnLabel }}
            text={`${t('create_a_user')} “${search}”`}
            onClick={() => {
              onCreate({ name: search });
              clearSearch();
            }}
          />
        );
      }}
      onSelect={(item) => onSelect(item as BasicUser)}
      queryFactory={usersQueryKeys.list}
      renderOption={(item) => <UserLookupCard inputSize={InputSize.medium} item={item} withContainer={false} />}
      setLocalSearch={setLocalSearch}
    />
  );
};

export const UserLookup = withStyles(styles)(forwardRef(UserLookupComponent));
